import { apiWrapper } from "@ch/api";
import {
  UsersControllerService,
  type UsersPartialExcluding_id_email_phone_,
  type UsersWithRelations,
} from "@ch/generated";
import { updateAppUserState } from "@ch/redux-store/user/actions";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { handleError } from "../utils/handleError";
import { NotifyError, NotifySuccess } from "../utils/helpers";
import useLoading from "./useLoading";
import useUserDetails from "./useUserDetails";

interface TInitiateUpdateUser {
  requestBody: UsersPartialExcluding_id_email_phone_;
  onSuccess?: (data: UsersWithRelations) => void;
  onFailure?: () => void;
  showConfirmation?: boolean;
}

const useUpdateUser = () => {
  const { loading, startLoading, stopLoading } = useLoading();
  const dispatch = useDispatch();
  const { user } = useUserDetails();

  const initiateUpdateUser = useCallback(
    async ({
      requestBody,
      onSuccess,
      onFailure,
      showConfirmation = true,
    }: TInitiateUpdateUser) => {
      startLoading();
      try {
        if (!user?.id) {
          NotifyError("User not found");
          return;
        }

        const data = (await apiWrapper(() =>
          UsersControllerService.usersControllerUpdateById({
            id: user.id!,
            requestBody,
          }),
        )) as unknown as UsersWithRelations;

        dispatch(
          updateAppUserState({
            currentUser: { ...user, ...requestBody },
          }),
        );

        if (showConfirmation) {
          NotifySuccess("Changes saved", {
            description: "Your updates have been saved successfully.",
          });
        }

        onSuccess?.(data);
      } catch (error) {
        handleError(error);
        onFailure?.();
      } finally {
        stopLoading();
      }
    },
    [dispatch, startLoading, stopLoading, user],
  );

  return {
    loading,
    initiateUpdateUser,
  };
};

export default useUpdateUser;
